import { StoryblokServerComponent } from "@storyblok/react/rsc";
import type { ComponentProps } from "react";
import { cn } from "~utils";
import { ImageItem } from "../image-item/image-item";
import type { SectionStackSchemaProps } from "./types";

type ClassName = ComponentProps<"div">["className"];

function SectionStack({ blok, ...rest }: Readonly<SectionStackSchemaProps>) {
  // THEME
  const theme = blok.theme;

  // GAP
  const gap: ClassName = `${blok.gapRow} ${blok.gapColumn}`;

  // DIRECTION
  const getDirectionClasses = (): ClassName => {
    if (blok.direction === "col") {
      return "section-stack-col";
    }
  };

  const direction = getDirectionClasses();

  // SECTION ID
  const sectionId = blok.id ? { id: blok.id } : {};

  // ITEMS
  const hasTopItems = (blok.topItems?.length ?? 0) > 0;
  const hasItems = (blok.items?.length ?? 0) > 0;
  const hasBottomItems = (blok.bottomItems?.length ?? 0) > 0;

  // BACKGROUND IMAGE
  const backgroundImageBlok = blok?.backgroundImage?.[0];

  // HEIGHT
  const height = blok.height ?? "auto";

  // ALIGNMENT
  const alignment = `${blok.justifyContent ?? ""} ${blok.alignItems ?? ""}`;

  // TEXT ALIGN
  const textAlign = blok.textAlign ?? "text-left";

  // BACKGROUND
  const backgroundColor = blok.backgroundColor
    ? { backgroundColor: blok.backgroundColor }
    : {};

  // PADDING
  const padding = `${blok.paddingTop ?? ""} ${blok.paddingRight ?? ""} ${
    blok.paddingBottom ?? ""
  } ${blok.paddingLeft ?? ""}`;

  return (
    <section
      {...sectionId}
      className={cn(
        "section-stack relative flex w-full flex-col overflow-hidden pt-[var(--section-py)] pb-[var(--section-py)]",
        theme,
        gap,
        alignment,
        textAlign,
        padding,
      )}
      style={{ height, ...backgroundColor }}
    >
      {/* TOP ITEMS */}
      {hasTopItems && (
        <div className="z-10 px-[var(--section-offset-x)]">
          {blok?.topItems?.map(nestedBlok => (
            <StoryblokServerComponent
              blok={nestedBlok}
              key={nestedBlok._uid}
              {...rest}
            />
          ))}
        </div>
      )}

      {hasItems && (
        <div className={cn("section-stack-row z-10", gap, direction)}>
          {blok.items.map(nestedBlok => (
            <StoryblokServerComponent
              blok={nestedBlok}
              key={nestedBlok._uid}
              {...rest}
            />
          ))}
        </div>
      )}

      {/* BOTTOM ITEMS */}
      {hasBottomItems && (
        <div className="z-10 flex px-[var(--section-offset-x)]">
          {blok?.bottomItems?.map(nestedBlok => (
            <StoryblokServerComponent
              blok={nestedBlok}
              key={nestedBlok._uid}
              {...rest}
            />
          ))}
        </div>
      )}

      {/* BACKGROUND IMAGE */}
      {backgroundImageBlok && (
        <div className="absolute top-[0] grid h-full w-full">
          <ImageItem blok={backgroundImageBlok} {...rest} />
        </div>
      )}
    </section>
  );
}

export { SectionStack };
